import type React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  PackageCheck,
  ClipboardList,
  Users,
  ScanLine,
  Settings,
  CreditCard,
  ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/enums';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  tourId: string;
  managerOnly?: boolean;
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Панель управления', icon: <LayoutDashboard className="w-5 h-5" />, tourId: 'staff:nav-dashboard' },
  { path: '/orders', label: 'Заказы', icon: <Package className="w-5 h-5" />, tourId: 'staff:nav-orders' },
  { path: '/receiving', label: 'Приёмка', icon: <PackageCheck className="w-5 h-5" />, tourId: 'staff:nav-receiving' },
  { path: '/inventory', label: 'Инвентаризация', icon: <ClipboardList className="w-5 h-5" />, tourId: 'staff:nav-inventory' },
  { path: '/clients', label: 'Клиенты', icon: <Users className="w-5 h-5" />, tourId: 'staff:nav-clients' },
  { path: '/scanner', label: 'Сканер', icon: <ScanLine className="w-5 h-5" />, tourId: 'staff:nav-scanner' },
  { path: '/settings', label: 'Настройки', icon: <Settings className="w-5 h-5" />, tourId: 'staff:nav-settings' },
  { path: '/billing', label: 'Биллинг', icon: <CreditCard className="w-5 h-5" />, tourId: 'staff:nav-billing', managerOnly: true },
];

const Sidebar: React.FC = () => {
  const { isSidebarOpen, isMobile, toggleSidebar, closeSidebar } = useUIStore();
  const { user } = useAuthStore();

  const handleNavClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        data-tour="staff:sidebar"
        className={cn(
          'fixed left-0 top-0 z-50 h-screen bg-white border-r border-gray-100 transition-all duration-300 flex flex-col',
          // Mobile: slide-in drawer
          isMobile && !isSidebarOpen && '-translate-x-full',
          isMobile && isSidebarOpen && 'translate-x-0 w-64',
          // Desktop: collapsible sidebar
          !isMobile && (isSidebarOpen ? 'w-64' : 'w-[72px]'),
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">AF</span>
            </div>
            {(isSidebarOpen || isMobile) && (
              <span className="font-semibold text-gray-900 text-lg truncate">AFlow</span>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.filter(item => !item.managerOnly || user?.role === UserRole.MANAGER).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  !isMobile && !isSidebarOpen && 'justify-center px-0',
                )
              }
              title={item.label}
              data-tour={item.tourId}
            >
              {item.icon}
              {(isSidebarOpen || isMobile) && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle — desktop only */}
        {!isMobile && (
          <div className="p-3 border-t border-gray-100 shrink-0">
            <button
              onClick={toggleSidebar}
              className="flex items-center justify-center w-full p-2.5 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              aria-label={isSidebarOpen ? 'Свернуть' : 'Развернуть'}
            >
              <ChevronLeft className={cn('w-5 h-5 transition-transform', !isSidebarOpen && 'rotate-180')} />
              {isSidebarOpen && <span className="ml-2 text-sm">Свернуть</span>}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
